import { useSnackbar } from "notistack";
import { useCanListWifAuthBindings } from "~/lib/iam/actions/mondoo/captain/Captain/ListWIFAuthBindings";
import { useCanCreateWifAuthBinding } from "~/lib/iam/actions/mondoo/captain/Captain/CreateWIFAuthBinding";
import { useCanRemoveWifAuthBinding } from "~/lib/iam/actions/mondoo/captain/Captain/RemoveWIFAuthBinding";
import {
  AuthBindingOrderField,
  GetWifAuthBindingsDocument,
  OrderDirection,
  useGetWifAuthBindingsQuery,
  useRemoveWifAuthBindingMutation,
  WifAuthBindingOrder,
} from "~/operations";
import { useSearch } from "~/components/search/useSearch";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { WifScope } from "~/pages/space-settings/WorkloadIdentity/constants";

type UseWorkloadIdentityParams = {
  scope: WifScope;
};

export function useWorkloadIdentity({ scope }: UseWorkloadIdentityParams) {
  const { enqueueSnackbar } = useSnackbar();
  const search = useSearch();
  const { handleSortClick, orderBy } = useSort<WifAuthBindingOrder>({
    defaultSort: {
      field: AuthBindingOrderField.CreateTime,
      direction: OrderDirection.Desc,
    },
    validFields: [
      AuthBindingOrderField.CreateTime,
      AuthBindingOrderField.Name,
      AuthBindingOrderField.LastUsed,
    ],
  });

  const hasWIFListPermissions = useCanListWifAuthBindings(scope);

  const hasWIFCreatePermissions = useCanCreateWifAuthBinding(scope);

  const hasWIFRemovePermissions = useCanRemoveWifAuthBinding(scope);
  const hasWIFSearchPermissions = false;

  const { data, loading } = useGetWifAuthBindingsQuery({
    variables: { scopeMrn: scope.mrn, orderBy },
    skip: !hasWIFListPermissions,
    fetchPolicy: "cache-and-network",
  });

  const [removeWIFAuthBinding, { loading: isRemoving }] =
    useRemoveWifAuthBindingMutation({
      refetchQueries: [GetWifAuthBindingsDocument],
    });

  const handleWIFAuthBindingRemove = async (mrn: string) => {
    try {
      await removeWIFAuthBinding({ variables: { mrn } });
      enqueueSnackbar(`Successfully removed workload identity provider.`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(
        `Something went wrong while attempting to remove the binding: ${error}`,
        { variant: "error" },
      );
      console.error(error);
    }
  };

  const bindings =
    data?.listWIFAuthBindings?.__typename === "ListWIFAuthBindingsPayload"
      ? data.listWIFAuthBindings.bindings || []
      : [];

  const isLoading = isRemoving || loading;

  return {
    bindings,
    handleWIFAuthBindingRemove,
    isLoading,
    showEmptyState: bindings.length === 0 && !isLoading,
    search,
    orderBy,
    handleSortClick,
    hasWIFListPermissions,
    hasWIFCreatePermissions,
    hasWIFSearchPermissions,
    hasWIFRemovePermissions,
  };
}
